import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, HStack, Text } from '@chakra-ui/react';
import html2canvas from 'html2canvas';
import { CopyChartButton, ChartWatermark } from '../../components/ChartSharing';
import { useColorModeValue } from '../../components/ui/color-mode';
import { formatEarningsValue } from './earningsFormatting';
import { addComparisonMetric, canUseAxis, comparisonScales, comparisonAxisLabel, SERIES_COLORS } from './earningsComparisonModel';

const selectStyle = { padding: '8px 10px', color: 'inherit', background: 'transparent', border: '1px solid #71809680', borderRadius: 6, minWidth: 0, maxWidth: '100%' };
const axisName = axis => axis === 'left' ? 'Left axis' : 'Right axis';
const unitName = unit => unit?.replace('/shares', ' per share');
const display = (value, unit) => formatEarningsValue(value, 'abbreviated', unit);
const tickLabel = (value, unit) => display(Number(value.toPrecision(3)), unit);

export function MetricComparison({ row, series: configuredSeries, ticker, onChange }) {
  const series = configuredSeries || [{ ...row, id: 'single', type: 'bar', axis: 'left', color: SERIES_COLORS[0] }];
  function update(id, change) {
    onChange(series.map(item => item.id === id ? { ...item, ...change } : item));
  }
  const chartRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState('');
  useEffect(() => {
    if (copyStatus !== 'copied' && copyStatus !== 'error') return undefined;
    const timer = setTimeout(() => setCopyStatus(''), 2000);
    return () => clearTimeout(timer);
  }, [copyStatus]);
  const background = useColorModeValue('#ffffff', '#111827');
  const foreground = useColorModeValue('#1a202c', '#e5e7eb');
  async function copyImage() {
    if (copyStatus === 'copying') return;
    if (!navigator.clipboard?.write || !window.ClipboardItem) {
      setCopyStatus('error');
      return;
    }
    setCopyStatus('copying');
    try {
      const image = html2canvas(chartRef.current, { backgroundColor: background, scale: 2 })
        .then(canvas => new Promise((resolve, reject) => canvas.toBlob(
          blob => blob ? resolve(blob) : reject(new Error('Image conversion failed')), 'image/png'
        )));
      // Start the clipboard write during the click gesture, including on Safari.
      image.catch(() => {});
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': image })]);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  }

  const scales = comparisonScales(series);
  const periods = series[0].comparisons;
  const axes = ['left', 'right'].filter(axis => series.some(item => item.axis === axis));
  const bars = series.filter(item => item.type === 'bar');
  const barWidth = Math.min(48, 144 / (bars.length || 1));
  const x = index => 190 + index * 260;
  const title = series.length === 1 ? `${series[0].label} — quarterly comparison` : 'Quarterly comparison';
  return <Box ref={chartRef} position="relative" p={{ base: 3, md: 5 }} pb={9} style={{ backgroundColor: background, color: foreground }}>
    <HStack justify="space-between" align="start" gap={3} mb={2}>
      <Box minW={0}>
        {ticker && <Text fontSize="sm" mb={1}>{ticker.toUpperCase()}</Text>}
        <Heading fontSize="20px" lineHeight="1.35">{title}</Heading>
      </Box>
      <CopyChartButton status={copyStatus} onClick={copyImage} />
    </HStack>
    <HStack aria-label="Chart legend" justify="center" gap={5} flexWrap="wrap" mt={4} fontSize="sm">
      {series.map(item => <span key={item.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <span aria-hidden="true" style={{ width: 18, height: item.type === 'line' ? 3 : 10, background: item.color, borderRadius: 2 }} />
        {item.label}
      </span>)}
    </HStack>
    <Box overflowX="auto" mt={2}>
      <svg viewBox="0 0 900 350" role="img" aria-label={`${series.map(item => item.label).join(', ')}: prior-year quarter, previous quarter, current quarter`} style={{ width: '100%', minWidth: 620, display: 'block' }}>
        {scales[axes[0]].ticks.map(tick => <line key={tick} x1="90" x2="810" y1={scales[axes[0]].y(tick)} y2={scales[axes[0]].y(tick)} stroke="currentColor" opacity="0.1" />)}
        {axes.map(axis => {
          const { ticks, y } = scales[axis];
          const unit = series.find(item => item.axis === axis).unit;
          return <g key={axis} aria-label={`${comparisonAxisLabel(series, axis)}: ${unitName(unit)}`}>
            <text transform={`translate(${axis === 'left' ? 16 : 884}, 165) rotate(${axis === 'left' ? -90 : 90})`} textAnchor="middle" fill="currentColor" fontSize="12">{comparisonAxisLabel(series, axis)} · {unitName(unit)}</text>
            {ticks.map(tick => <text key={tick} x={axis === 'left' ? 80 : 820} y={y(tick) + 4} textAnchor={axis === 'left' ? 'end' : 'start'} fill="currentColor" fontSize="11">{tickLabel(tick, unit)}</text>)}
          </g>;
        })}
        <line x1="90" x2="810" y1={scales[axes[0]].y(0)} y2={scales[axes[0]].y(0)} stroke="currentColor" opacity="0.45" />
        {periods.map((period, index) => <g key={index}>
          <text x={x(index)} y="308" textAnchor="middle" fill="currentColor" fontSize="13">{period.label}</text>
          <text x={x(index)} y="329" textAnchor="middle" fill="currentColor" fontSize="11" opacity="0.65">{period.end || 'Period unavailable'}</text>
        </g>)}
        {bars.map((item, barIndex) => item.comparisons.map((period, index) => {
          const y = scales[item.axis].y;
          const value = period.value;
          return <g key={`${item.id}:${index}`} data-series={item.id}>
            <title>{`${item.label} · ${period.label}: ${display(value, item.unit)} · ${period.basis}`}</title>
            {Number.isFinite(value) && <rect x={x(index) - bars.length * barWidth / 2 + barIndex * barWidth + 2} width={barWidth - 4} y={Math.min(y(value), y(0))} height={Math.max(Math.abs(y(value) - y(0)), 1)} fill={item.color} rx="2" />}
          </g>;
        }))}
        {series.filter(item => item.type === 'line').map(item => {
          const y = scales[item.axis].y;
          // Start a new path after a missing value; don't connect across missing quarters.
          const path = item.comparisons.map((period, index) => Number.isFinite(period.value)
            ? `${index && Number.isFinite(item.comparisons[index - 1].value) ? 'L' : 'M'} ${x(index)} ${y(period.value)}` : '').join(' ');
          return <g key={item.id} data-series={item.id}>
            <path d={path} fill="none" stroke={item.color} strokeWidth="3" strokeLinejoin="round" />
            {item.comparisons.map((period, index) => <g key={index}>
              <title>{`${item.label} · ${period.label}: ${display(period.value, item.unit)} · ${period.basis}`}</title>
              {Number.isFinite(period.value) && <circle cx={x(index)} cy={y(period.value)} r="5" fill={item.color} stroke={background} strokeWidth="2" />}
            </g>)}
          </g>;
        })}
      </svg>
    </Box>
    <Box overflowX="auto" mt={2}>
      <table aria-label="Quarterly comparison values" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr><th style={{ textAlign: 'left', padding: 8 }}>Metric</th>{onChange && <th data-html2canvas-ignore="true" style={{ padding: 8 }}>Chart</th>}{periods.map((period, i) => <th key={i} style={{ textAlign: 'right', padding: 8 }}>{period.label}</th>)}{onChange && <th data-html2canvas-ignore="true" style={{ padding: 8 }}>Axis</th>}</tr></thead>
        <tbody>{series.map(item => <tr key={item.id} style={{ borderTop: '1px solid #71809630' }}>
          <th style={{ textAlign: 'left', padding: 8, fontWeight: 500 }}><span aria-hidden="true" style={{ color: item.color }}>● </span>{item.label}</th>
          {onChange && <td data-html2canvas-ignore="true" style={{ padding: 8 }}>
        <HStack role="group" aria-label={`${item.label} chart type`} gap={0} p="3px" borderWidth="1px" borderRadius="lg" width="fit-content">
          {['bar', 'line'].map(type => <Button key={type} size="sm" variant={item.type === type ? 'solid' : 'ghost'}
            aria-label={`${item.label} ${type === 'bar' ? 'Bar' : 'Line'}`} aria-pressed={item.type === type}
            onClick={() => update(item.id, { type })} borderRadius="md" minW="56px">
            {type === 'bar' ? 'Bar' : 'Line'}
          </Button>)}
        </HStack>
          </td>}
          {item.comparisons.map((period, i) => <td key={i} title={`${period.basis}. ${formatEarningsValue(period.value, 'full', item.unit)}`} style={{ textAlign: 'right', padding: 8, fontVariantNumeric: 'tabular-nums' }}>{display(period.value, item.unit)}</td>)}
          {onChange && <td data-html2canvas-ignore="true" style={{ padding: 8 }}><HStack gap={1}>
        <select aria-label={`${item.label} axis`} value={item.axis} onChange={event => update(item.id, { axis: event.target.value })} style={{ ...selectStyle, background }}>
          {['left', 'right'].map(axis => <option key={axis} value={axis} disabled={!canUseAxis(series, item, axis)}>{axisName(axis)}</option>)}
        </select>
        <Button aria-label={`Remove ${item.label}`} size="sm" variant="ghost" onClick={() => onChange(series.filter(metric => metric.id !== item.id))}>×</Button>
          </HStack></td>}
        </tr>)}</tbody>
      </table>
    </Box>
    {series.filter(item => item.description).map(item => <Box key={item.id} fontSize="xs" color="fg.muted" mt={3}>{item.label}: {item.description} {item.source_url && <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Source statement ↗</a>}</Box>)}
    <ChartWatermark />
  </Box>;
}

export default function EarningsComparison({ metrics, series, onChange, ticker }) {
  const [query, setQuery] = useState('');
  const background = useColorModeValue('#ffffff', '#111827');
  const available = metrics.filter(metric => !series.some(item => item.id === metric.id));
  const filtered = available.filter(metric => `${metric.label} ${metric.group}`.toLowerCase().includes(query.toLowerCase()));
  const revenue = metrics.find(metric => metric.kind === 'income' && /^(Revenue|Revenues|Net sales|Total revenue)$/i.test(metric.label));
  const income = metrics.find(metric => metric.kind === 'income' && /^(Net income|Net income \(loss\)|Net loss)$/i.test(metric.label));
  const fcf = metrics.find(metric => metric.id === 'free-cash-flow');
  const presetAvailable = revenue && income && fcf && revenue.unit === income.unit && income.unit === fcf.unit;
  function preset() {
    onChange([income, fcf, revenue].map((metric, index) => ({ ...metric, color: SERIES_COLORS[index], type: index === 2 ? 'line' : 'bar', axis: index === 2 ? 'right' : 'left' })));
  }
  return <Box id="earnings-comparison" borderWidth="1px" borderRadius="xl" overflow="hidden" mb={8} scrollMarginTop="24px">
    <Box p={{ base: 3, md: 5 }} borderBottomWidth={series.length ? '1px' : '0'}>
      <HStack justify="space-between" flexWrap="wrap" gap={3} mb={2}>
        <Heading fontSize="22px">Compare metrics</Heading>
        {presetAvailable && <Button size="sm" variant="outline" onClick={preset}>Revenue + net income + free cash flow</Button>}
      </HStack>
      <Text color="fg.muted" fontSize="sm" mb={4}>Combine up to six metrics. Choose a chart type and axis for each.</Text>
      <HStack flexWrap="wrap" gap={3} mb={series.length ? 4 : 0}>
        <input aria-label="Search comparison metrics" placeholder="Search metrics…" value={query} onChange={event => setQuery(event.target.value)} style={{ ...selectStyle, flex: '1 1 180px', background }} />
        <select aria-label="Add metric" value="" disabled={series.length >= SERIES_COLORS.length || !available.length} onChange={event => { const metric = metrics.find(item => item.id === event.target.value); if (metric) onChange(addComparisonMetric(series, metric)); setQuery(''); }} style={{ ...selectStyle, flex: '2 1 240px', background }}>
          <option value="">{series.length >= SERIES_COLORS.length ? 'Six metrics selected' : filtered.length ? 'Add a metric…' : 'No matching metrics'}</option>
          {[...new Set(filtered.map(metric => metric.group))].map(group => <optgroup key={group} label={group}>
            {filtered.filter(metric => metric.group === group).map(metric => <option key={metric.id} value={metric.id} disabled={!['left', 'right'].some(axis => canUseAxis(series, metric, axis))}>{metric.label} ({unitName(metric.unit)})</option>)}
          </optgroup>)}
        </select>
        {series.length > 0 && <Button size="sm" variant="ghost" onClick={() => onChange([])}>Clear all</Button>}
      </HStack>
      {new Set(series.map(item => item.unit)).size > 1 && <Text fontSize="xs" color="fg.muted" mt={2}>Metrics sharing an axis must use the same unit.</Text>}
      {!series.length && <Text fontSize="sm" color="fg.muted" mt={4}>Add metrics above or choose “Compare quarters” in any statement below.</Text>}
    </Box>
    {series.length > 0 && <MetricComparison series={series} ticker={ticker} onChange={onChange} />}
  </Box>;
}
